import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Star, Trash2, Plus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";

export function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(5);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/reviews").then(({ data }) => setReviews(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    await api.patch(`/admin/reviews/${r.id}`, { is_visible: !r.is_visible });
    load();
  };
  const del = async (r) => { await api.delete(`/admin/reviews/${r.id}`); load(); };
  const add = async () => {
    setSaving(true);
    try {
      await api.post("/admin/reviews", { name: name || "Sites UX Client", rating, text });
      toast.success("Review added");
      setName(""); setText(""); setRating(5); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div className="border border-white/10 bg-[#0A0A0B] p-6 md:col-span-1">
        <h3 className="font-display font-semibold">Add your own review</h3>
        <Label className="mt-4 block text-zinc-400">Name</Label>
        <Input data-testid="admin-review-name" value={name} onChange={(e) => setName(e.target.value)}
          className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="Client name" />
        <Label className="mt-4 block text-zinc-400">Rating</Label>
        <div className="mt-1.5 flex gap-1">
          {[1, 2, 3, 4, 5].map((s) => (
            <button key={s} type="button" data-testid={`admin-review-star-${s}`} onClick={() => setRating(s)}>
              <Star className={`h-6 w-6 ${s <= rating ? "fill-primary text-primary" : "text-zinc-700"}`} />
            </button>
          ))}
        </div>
        <Label className="mt-4 block text-zinc-400">Review</Label>
        <Textarea data-testid="admin-review-text" rows={4} value={text} onChange={(e) => setText(e.target.value)}
          className="mt-1.5 rounded-sm border-white/10 bg-black" placeholder="Review text" />
        <Button data-testid="admin-review-add-btn" onClick={add} disabled={saving || !text.trim()}
          className="mt-3 w-full rounded-full bg-primary text-white hover:bg-primary/90">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Add review
        </Button>
      </div>
      <div className="border border-white/10 bg-[#0A0A0B] p-6 md:col-span-2">
        <h3 className="font-display font-semibold">
          All reviews <span className="text-sm text-zinc-500">({reviews.length})</span>
        </h3>
        <p className="mt-1 text-xs text-zinc-500">Toggle to show or hide each review on the homepage.</p>
        <div className="mt-4 space-y-2">
          {reviews.length === 0 && <p className="text-sm text-zinc-500">No reviews yet.</p>}
          {reviews.map((r) => (
            <div key={r.id} data-testid={`admin-review-${r.id}`} className="border border-white/10 bg-black p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star key={s} className={`h-3.5 w-3.5 ${s <= r.rating ? "fill-primary text-primary" : "text-zinc-700"}`} />
                    ))}
                  </div>
                  <p className="mt-2 text-sm text-zinc-300">"{r.text}"</p>
                  <p className="mt-2 font-mono text-[11px] text-zinc-500">
                    {r.show_name ? r.name : "Anonymous"}
                    {r.show_email && r.email ? ` · ${r.email}` : ""}
                    {r.is_admin_added ? " · added by you" : ""}
                    {r.created_at ? ` · ${format(parseISO(r.created_at), "MMM d")}` : ""}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase text-zinc-500">{r.is_visible ? "Shown" : "Hidden"}</span>
                    <Switch data-testid={`review-toggle-${r.id}`} checked={r.is_visible} onCheckedChange={() => toggle(r)} />
                  </div>
                  <Button size="icon" variant="ghost" data-testid={`review-delete-${r.id}`} onClick={() => del(r)}
                    className="h-8 w-8 text-zinc-400 hover:text-destructive">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
